// src/lib/format.ts (T1): dates, times and small text helpers for every screen (spec 2.9). Dates and times
// render in the tenant's time zone (tenant.region.timezone), which session.boot() sets with setTimeZone().
// Every function takes an optional `now` so tests and screens agree on "today".

let zone: string | undefined;

/** The zone every date renders in. Unset (before boot) means the browser's own zone. */
export function setTimeZone(tz: string | undefined): void {
  zone = tz && isValidZone(tz) ? tz : undefined;
}
export function timeZone(): string | undefined {
  return zone;
}
function isValidZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

type DateInput = string | number | Date;
function toDate(d: DateInput): Date {
  return d instanceof Date ? d : new Date(d);
}

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify([zone ?? "", opts]);
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", zone ? { ...opts, timeZone: zone } : opts);
    cache.set(key, f);
  }
  return f;
}

/** The calendar day of an instant in the tenant zone, as "YYYY-MM-DD" (for day comparisons). */
export function dayKey(d: DateInput): string {
  const parts = fmt({ year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(toDate(d));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Whole calendar days from `d` to `now` in the tenant zone (0 today, 1 yesterday, negative in the future). */
export function daysAgo(d: DateInput, now: DateInput = Date.now()): number {
  const a = Date.parse(dayKey(d) + "T00:00:00Z");
  const b = Date.parse(dayKey(now) + "T00:00:00Z");
  return Math.round((b - a) / 86_400_000);
}

export type DateStyle =
  | "short"      // Sep 22
  | "day"        // Tue, Sep 22
  | "weekday"    // Tuesday
  | "dayLong"    // Tuesday, September 22
  | "long"       // September 22, 2026
  | "monthDay"   // September 22
  | "medium";    // Sep 22, 2026

const DATE_OPTS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric" },
  day: { weekday: "short", month: "short", day: "numeric" },
  weekday: { weekday: "long" },
  dayLong: { weekday: "long", month: "long", day: "numeric" },
  long: { month: "long", day: "numeric", year: "numeric" },
  monthDay: { month: "long", day: "numeric" },
  medium: { month: "short", day: "numeric", year: "numeric" },
};

export function formatDate(d: DateInput, style: DateStyle = "short"): string {
  return fmt(DATE_OPTS[style]).format(toDate(d));
}

/** 1:15 PM */
export function formatTime(d: DateInput): string {
  return fmt({ hour: "numeric", minute: "2-digit" }).format(toDate(d));
}

/** "Sep 22 at 1:15 PM" (with the year when it is not this year). */
export function formatDateTime(d: DateInput, now: DateInput = Date.now()): string {
  const sameYear = dayKey(d).slice(0, 4) === dayKey(now).slice(0, 4);
  return `${formatDate(d, sameYear ? "short" : "medium")} at ${formatTime(d)}`;
}

/**
 * A compact "when" for lists and feeds: today the time ("1:15 PM"), then "Yesterday", the weekday within a
 * week ("Sat"), the date this year ("Sep 13"), else the date with the year ("Sep 13, 2025").
 */
export function shortWhen(d: DateInput, now: DateInput = Date.now()): string {
  const days = daysAgo(d, now);
  if (days <= 0) return formatTime(d);
  if (days === 1) return "Yesterday";
  if (days < 7) return fmt({ weekday: "short" }).format(toDate(d));
  const sameYear = dayKey(d).slice(0, 4) === dayKey(now).slice(0, 4);
  return formatDate(d, sameYear ? "short" : "medium");
}

/** "Just now", "12 min ago", "3 hours ago", "Yesterday", "4 days ago", then a date. */
export function relativeTime(d: DateInput, now: DateInput = Date.now()): string {
  const ms = toDate(now).getTime() - toDate(d).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const days = daysAgo(d, now);
  if (days <= 0) {
    const h = Math.floor(min / 60);
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return shortWhen(d, now);
}

/** A feed's day heading: "Today", "Yesterday", else "Tuesday, September 22". */
export function dayHeading(d: DateInput, now: DateInput = Date.now()): string {
  const days = daysAgo(d, now);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  const sameYear = dayKey(d).slice(0, 4) === dayKey(now).slice(0, 4);
  return sameYear ? formatDate(d, "dayLong") : formatDate(d, "long");
}

/** "Good morning" before noon, "Good afternoon" until 5 PM, then "Good evening", in the tenant zone. */
export function greeting(now: DateInput = Date.now()): string {
  const h = Number(fmt({ hour: "numeric", hourCycle: "h23" }).format(toDate(now)));
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** A video length: 40 → "0:40", 125 → "2:05". */
export function duration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ------------------------------------------------------------------ text

/** ["Dana"] "Dana"; ["Dana", "Chris"] "Dana and Chris"; three or more "Lower Level, Home Office and Garage". */
export function joinNames(names: readonly string[]): string {
  const list = names.filter((n) => n.trim() !== "");
  if (list.length <= 1) return list[0] ?? "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/** plural(1, "idea") "1 idea"; plural(3, "idea") "3 ideas"; plural(2, "person", "people") "2 people". */
export function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : (many ?? one + "s")}`;
}

const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
/** 7 "seven" (0 to 12 in words, larger as digits); `capital` gives "Seven". */
export function numberWord(n: number, capital = false): string {
  const w = Number.isInteger(n) && n >= 0 && n < WORDS.length ? WORDS[n]! : String(n);
  return capital ? w.charAt(0).toUpperCase() + w.slice(1) : w;
}

const SMALL = new Set(["a", "an", "and", "as", "at", "but", "by", "for", "in", "nor", "of", "on", "or", "the", "to", "with", "&"]);
/**
 * Title Case for headings and room names (Direction D: "Room names are Title Case everywhere"): "Kitchen &
 * dining" becomes "Kitchen & Dining", "The land" becomes "The Land". Small words stay lower case except first
 * and last; words that already carry capitals ("LEED", "iPad") are kept.
 */
export function titleCase(s: string): string {
  const words = s.trim().split(/(\s+)/);
  const real = words.filter((w) => !/^\s+$/.test(w));
  let i = 0;
  return words
    .map((w) => {
      if (/^\s+$/.test(w)) return w;
      const idx = i++;
      if (/[A-Z]/.test(w.slice(1)) || /^[^a-z]*$/.test(w)) return w;
      const lower = w.toLowerCase();
      if (idx !== 0 && idx !== real.length - 1 && SMALL.has(lower)) return lower;
      return w.replace(/^([^\p{L}]*)(\p{L})/u, (_m, pre: string, ch: string) => pre + ch.toUpperCase());
    })
    .join("");
}

/** The builder's or team's monogram for its avatar ("MA", and "HP" for "Harbor & Pine Homes"): shared/format.ts. */
export { monogram } from "../../shared/format";
